import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';

export type VersionDiffLine = {
  type: 'context' | 'add' | 'remove';
  text: string;
};

export type VersionDiffHunk = {
  oldStart: number;
  oldLines: number;
  newStart: number;
  newLines: number;
  lines: VersionDiffLine[];
};

export type VersionDiffMetaChange = {
  key: string;
  before: string;
  after: string;
};

export type VersionDiffSide = {
  id: string;
  source: 'current' | 'history' | 'revision';
};

export type VersionDiffFileChange = 'added' | 'removed' | 'modified' | 'unchanged';

export type VersionDiffFile = {
  path: string;
  kind?: 'BINARY';
  beforeSizeBytes?: number | null;
  afterSizeBytes?: number | null;
  mimeType?: string | null;
  change: VersionDiffFileChange;
  hunks: VersionDiffHunk[];
  stats: { added: number; removed: number };
  truncated: boolean;
};

export type VersionDiff = {
  from: VersionDiffSide;
  to: VersionDiffSide;
  metaChanges: VersionDiffMetaChange[];
  hunks: VersionDiffHunk[];
  stats: { added: number; removed: number };
  formatOnlyChange: boolean;
  truncated: boolean;
  files?: VersionDiffFile[];
};

type VersionDiffResponse = { data: VersionDiff };

export type VersionDiffQuery = {
  against?: string;
  summaryOnly?: boolean;
};

const withQuery = (url: string, query?: VersionDiffQuery): string => {
  const params = new URLSearchParams();
  if (query?.against) {
    params.set('against', query.against);
  }
  if (query?.summaryOnly) {
    params.set('summaryOnly', 'true');
  }
  const suffix = params.toString();
  return suffix.length > 0 ? `${url}?${suffix}` : url;
};

const fetchVersionDiff = async (token: string, url: string): Promise<VersionDiff> => {
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch version diff: ${response.status}`);
  }
  return ((await response.json()) as VersionDiffResponse).data;
};

export const fetchSkillVersionDiff = (
  token: string,
  projectId: string,
  skillId: string,
  historyId: string,
  query?: VersionDiffQuery,
): Promise<VersionDiff> =>
  fetchVersionDiff(
    token,
    withQuery(`${getApiUrl()}/api/projects/${projectId}/skills/${skillId}/history/${historyId}/diff`, query),
  );

export const fetchConventionVersionDiff = (
  token: string,
  projectId: string,
  conventionId: string,
  historyId: string,
  query?: VersionDiffQuery,
): Promise<VersionDiff> =>
  fetchVersionDiff(
    token,
    withQuery(`${getApiUrl()}/api/projects/${projectId}/conventions/${conventionId}/history/${historyId}/diff`, query),
  );

export const fetchDocumentVersionDiff = (
  token: string,
  projectId: string,
  documentId: string,
  revisionId: string,
  query?: VersionDiffQuery,
): Promise<VersionDiff> =>
  fetchVersionDiff(
    token,
    withQuery(`${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/diff`, query),
  );
